import React, { useState } from 'react';
import PublicDonationForm from './PublicDonationForm';
import { copyText } from '@opendate-app/setlist';

const formatCurrency = (cents) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
};

const PublicGivingCampaign = ({
  id, name, description, status, headline, primary_color, organizer_name,
  goal_amount_cents, total_raised_cents, progress_percentage,
  donor_count, days_remaining, past_end_date,
  cover_image_url, milestones, is_active,
  format, preset_amounts, enabled_frequencies, button_text,
  dedication_enabled, dedication_options, preset_amount_descriptions, show_other_amount, gift_questions,
  team_info, nonProduction, powered_by_logo_url,
  donationsCreateUrl, recurringDonationsCreateUrl, feeRules, stripePublishableKey, stripeConnectedAccountId, turnstileSiteKey, canDonate
}) => {
  const [copied, setCopied] = useState(false);
  const [verifiedOpen, setVerifiedOpen] = useState(false);

  const isFormFormat = format === 'form';
  const progressPct = Math.min(progress_percentage || 0, 100);
  const hasGoal = goal_amount_cents > 0;
  const color = primary_color || '#1982C4';
  const style = { '--gc-primary': color };

  const handleShare = (platform) => {
    const url = window.location.href;
    const text = `Support ${name}!`;
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      email: `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(`Check out this campaign: ${url}`)}`,
    };
    if (platform === 'copy') {
      copyText(url).then((ok) => {
        if (!ok) return;
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    } else {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
  };

  // Form format: two-column GiveButter-style layout
  if (isFormFormat) {
    return (
      <div className="gc-public gc-public--form" style={style}>
        <div className="gc-form-layout">
          {/* Left column: single card with image, progress, description, org footer */}
          <div className="gc-form-left">
            <div className="gc-form-card">
              {/* Cover image at top of card */}
              {cover_image_url && (
                <img src={cover_image_url} alt={name} className="gc-form-cover-image" />
              )}

              {/* Progress bar (if goal is set) */}
              {hasGoal && (
                <div className="gc-form-card-body">
                  <div className="gc-form-progress-row">
                    <span className="gc-form-progress-amount">{formatCurrency(total_raised_cents)}</span>
                    <span className="gc-form-progress-goal">{progress_percentage}% of {formatCurrency(goal_amount_cents)} goal</span>
                  </div>
                  <div className="gc-progress-bar">
                    <div className="gc-progress-fill" style={{ width: `${progressPct}%` }} />
                  </div>
                </div>
              )}

              {/* Title and description */}
              <div className="gc-form-card-body">
                <h2 className="gc-form-title">{name}</h2>
                {description && (
                  <p className="gc-form-description">{description}</p>
                )}
              </div>

              {/* Organization footer */}
              {organizer_name && (
                <div className="gc-form-org-footer">
                  <div>
                    <div className="gc-form-org-name">
                      Organized by {organizer_name}
                    </div>
                    {team_info && team_info.business_type && (
                      <div className="gc-form-org-details">
                        {team_info.business_type}
                      </div>
                    )}
                  </div>
                  <a className="gc-verified-badge" onClick={() => setVerifiedOpen(true)}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="8" fill="#6D9F1E"/>
                      <path d="M5 8.5L7 10.5L11 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Verified
                  </a>
                </div>
              )}

              {/* Verified modal */}
              {verifiedOpen && (
                <div className="gc-modal-overlay" onClick={() => setVerifiedOpen(false)}>
                  <div className="gc-verified-modal" onClick={(e) => e.stopPropagation()}>
                    <button className="gc-modal-close" onClick={() => setVerifiedOpen(false)}>&times;</button>
                    <div className="gc-verified-modal-header">
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <circle cx="20" cy="20" r="20" fill="#6D9F1E"/>
                        <path d="M12 21L17 26L28 15" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <h3>Verified Organization</h3>
                    </div>
                    <p className="gc-verified-modal-description">
                      This organization has been verified. Their identity and nonprofit status have been confirmed.
                    </p>
                    <dl className="gc-verified-modal-details">
                      <dt>Organization</dt>
                      <dd>{team_info && team_info.legal_business_name ? team_info.legal_business_name : organizer_name}</dd>
                      {team_info && team_info.business_type && (
                        <>
                          <dt>Type</dt>
                          <dd>{team_info.business_type}</dd>
                        </>
                      )}
                    </dl>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right column: donation form */}
          <div className="gc-form-right">
            <div className="gc-form-sticky">
              <PublicDonationForm
                campaignName={name}
                primaryColor={color}
                presetAmounts={preset_amounts}
                enabledFrequencies={enabled_frequencies}
                buttonText={button_text}
                dedicationEnabled={dedication_enabled}
                dedicationOptions={dedication_options}
                presetAmountDescriptions={preset_amount_descriptions}
                showOtherAmount={show_other_amount}
                donationsCreateUrl={donationsCreateUrl}
                recurringDonationsCreateUrl={recurringDonationsCreateUrl}
                feeRules={feeRules}
                stripePublishableKey={stripePublishableKey}
                stripeConnectedAccountId={stripeConnectedAccountId}
                turnstileSiteKey={turnstileSiteKey}
                canDonate={canDonate}
                nonProduction={nonProduction}
                giftQuestions={gift_questions || []}
              />
              <div className="gc-powered-footer">
                <span>Powered by</span>
                <img src={powered_by_logo_url} alt="Opendate" className="gc-powered-logo" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const scrollToDonate = () => {
    const el = document.getElementById('gc-donate');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="gc-public gc-fw" style={style}>
      <header className="gc-fw-header">
        <div className="gc-fw-header-org">
          <div className="gc-fw-header-name">{organizer_name || name}</div>
        </div>
      </header>

      <section className={`gc-fw-hero ${!cover_image_url ? 'gc-fw-hero--no-image' : ''}`}>
        {cover_image_url && (
          <div className="gc-fw-hero-image" style={{ backgroundImage: `url(${cover_image_url})` }} />
        )}
        <div className="gc-fw-hero-overlay" />
        <div className="gc-fw-hero-content">
          {organizer_name && (
            <div className="gc-fw-hero-meta">
              <span className="gc-fw-hero-org">Organized by {organizer_name}</span>
            </div>
          )}
          <h1>{name}</h1>
          {headline && <div className="gc-fw-hero-headline">{headline}</div>}
        </div>
      </section>

      <div className="gc-fw-statbar">
        <div className="gc-fw-statbar-inner">
          <div className="gc-fw-stats">
            <div className="gc-fw-stat">
              <div className="gc-fw-stat-value">{formatCurrency(total_raised_cents)}</div>
              <div className="gc-fw-stat-label">
                {hasGoal ? `raised of ${formatCurrency(goal_amount_cents)} goal` : 'raised'}
              </div>
            </div>
            {hasGoal && (
              <div className="gc-fw-stat">
                <div className="gc-fw-stat-value gc-fw-stat-value--accent">{progress_percentage}%</div>
                <div className="gc-fw-stat-label">of goal</div>
              </div>
            )}
            <div className="gc-fw-stat">
              <div className="gc-fw-stat-value">{donor_count || 0}</div>
              <div className="gc-fw-stat-label">supporters</div>
            </div>
            {is_active && !past_end_date && days_remaining != null && (
              <div className="gc-fw-stat">
                <div className="gc-fw-stat-value">{days_remaining}</div>
                <div className="gc-fw-stat-label">days left</div>
              </div>
            )}
          </div>
          {hasGoal && (
            <div className="gc-fw-statbar-bar">
              <div className="gc-progress-bar">
                <div className="gc-progress-fill" style={{ width: `${progressPct}%` }} />
              </div>
            </div>
          )}
          {is_active && canDonate !== false && (
            <button type="button" className="gc-fw-statbar-donate" onClick={scrollToDonate}>
              {button_text || 'Donate'}
            </button>
          )}
        </div>
      </div>

      {!is_active && (
        <div className="gc-fw-banner-wrap">
          <div className="gc-fw-banner">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" /></svg>
            <div>
              <div className="gc-fw-banner-title">This campaign is not currently accepting donations.</div>
              <div className="gc-fw-banner-body">
                {status === 'completed'
                  ? 'This campaign has ended. Thank you to all who contributed!'
                  : 'This campaign has not started yet. Check back soon!'}
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="gc-fw-main">
        <div className="gc-fw-col-main">
          {description && (
            <div className="gc-fw-card">
              <h2 className="gc-fw-card-title">Story</h2>
              <div className="gc-fw-story">{description}</div>
            </div>
          )}

          {milestones && milestones.length > 0 && (
            <div className="gc-fw-card">
              <h2 className="gc-fw-card-title">Campaign Milestones</h2>
              <ul className="gc-milestones-list">
                {milestones.map(m => {
                  const reached = (progress_percentage || 0) >= m.target_percentage;
                  return (
                    <li key={m.id} className="gc-milestone-item">
                      <div className={`gc-milestone-icon ${reached ? 'gc-milestone-icon--reached' : 'gc-milestone-icon--pending'}`}>
                        {reached ? '✓' : '○'}
                      </div>
                      <span className="gc-milestone-text">{m.label}</span>
                      <span className="gc-milestone-pct">{m.target_percentage}%</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="gc-fw-share">
            <span className="gc-fw-share-label">Share this campaign</span>
            <button type="button" className="gc-fw-share-btn" onClick={() => handleShare('facebook')}>Facebook</button>
            <button type="button" className="gc-fw-share-btn" onClick={() => handleShare('twitter')}>Twitter</button>
            <button type="button" className="gc-fw-share-btn" onClick={() => handleShare('email')}>Email</button>
            <button type="button" className="gc-fw-share-btn" onClick={() => handleShare('copy')}>{copied ? 'Copied!' : 'Copy link'}</button>
          </div>
        </div>

        <aside id="gc-donate" className="gc-fw-aside">
          <div className="gc-fw-donate-sticky">
            {is_active ? (
              <PublicDonationForm
                campaignName={name}
                primaryColor={color}
                presetAmounts={preset_amounts}
                enabledFrequencies={enabled_frequencies}
                buttonText={button_text}
                dedicationEnabled={dedication_enabled}
                dedicationOptions={dedication_options}
                presetAmountDescriptions={preset_amount_descriptions}
                showOtherAmount={show_other_amount}
                donationsCreateUrl={donationsCreateUrl}
                recurringDonationsCreateUrl={recurringDonationsCreateUrl}
                feeRules={feeRules}
                stripePublishableKey={stripePublishableKey}
                stripeConnectedAccountId={stripeConnectedAccountId}
                turnstileSiteKey={turnstileSiteKey}
                canDonate={canDonate}
                nonProduction={nonProduction}
                giftQuestions={gift_questions || []}
              />
            ) : (
              <div className="gc-fw-card gc-fw-inactive">
                <p>
                  {status === 'completed'
                    ? 'This campaign has ended. Thank you to all who contributed!'
                    : 'This campaign has not started yet. Check back soon!'}
                </p>
              </div>
            )}
            <div className="gc-powered-footer">
              <span>Powered by</span>
              <img src={powered_by_logo_url} alt="Opendate" className="gc-powered-logo" />
            </div>
          </div>
        </aside>
      </main>

      <footer className="gc-fw-footer">
        <div className="gc-fw-footer-text">
          {team_info && team_info.legal_business_name ? team_info.legal_business_name : (organizer_name || name)}
        </div>
      </footer>
    </div>
  );
};

export default PublicGivingCampaign;
